import React, { useCallback, useEffect, useRef } from 'react'
import { ScrollView } from 'react-native'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useFocusEffect } from '@react-navigation/native'
import { ClientMonitoringType } from '@/domain/models'
import { GetRemotyContacts, GetRemotyContactsParams, GetSigmaDeskContacts, GetSigmaDeskContactsParams } from '@/domain/usecases'
import { Box, CardList, PageHeader, Stack, ActivityIndicator, HStack, Text } from '@/presentation/components'
import { layoutStyles } from '@/presentation/styles/globals'
import { theme } from '@/presentation/styles'
import { useAuth, useErrorHandler, useMediaQueries, useThemeClient } from '@/presentation/hooks'
import { CardContact, contactListState, isLoadingContactEventState, SelectRemotyEventCompany, SelectSigmaCloudKey, selectSigmaCloudKeyData, remotyFilterState, contactListHasError, lastLoadedContactKeyState } from '@/presentation/pages/Events/components'
import { buildMonitoringTabRequestKey } from '../../request-key'

type ContactEventListProps = {
  getRemotyContacts: GetRemotyContacts
  getSigmaDeskContacts: GetSigmaDeskContacts
}

export const ContactEventList: React.FC<ContactEventListProps> = ({
  getRemotyContacts,
  getSigmaDeskContacts
}: ContactEventListProps) => {
  const { getClient } = useAuth()
  const handleError = useErrorHandler()
  const { isMobile } = useMediaQueries()
  const { getAccentColor } = useThemeClient()
  const client = getClient()

  const [hasError, setHasError] = useRecoilState(contactListHasError)
  const currentSigmaKey = useRecoilValue(selectSigmaCloudKeyData)
  const { remotyIdEquipamento } = useRecoilValue(remotyFilterState)
  const [contactList, setContactList] = useRecoilState(contactListState)
  const [isLoading, setLoading] = useRecoilState(isLoadingContactEventState)
  const lastLoadedKey = useRecoilValue(lastLoadedContactKeyState)
  const setLastLoadedKey = useSetRecoilState(lastLoadedContactKeyState)
  const previousRequestKeyRef = useRef('')

  const currentRequestKey = buildMonitoringTabRequestKey({
    clientType: client.tipoConexao,
    currentSigmaKey,
    remotyIdEquipamento
  })

  useEffect(() => {
    if (
      hasError &&
      previousRequestKeyRef.current !== '' &&
      previousRequestKeyRef.current !== currentRequestKey
    ) {
      setHasError(false)
    }

    previousRequestKeyRef.current = currentRequestKey
  }, [currentRequestKey, hasError, setHasError])

  useFocusEffect(
    useCallback(() => {
      const conditions = {
        [ClientMonitoringType.REMOTY]: remotyIdEquipamento && remotyIdEquipamento !== 0,
        [ClientMonitoringType.MONI]: false,
        [ClientMonitoringType.SIGMA_CLOUD]: !!(currentSigmaKey.codCliente),
        [ClientMonitoringType.SIGMA_DESK]: !!(currentSigmaKey.codCliente),
        [ClientMonitoringType.SO_WILL]: false
      }[client.tipoConexao]

      if (conditions && !hasError && currentRequestKey !== lastLoadedKey) { getEventContacts(currentRequestKey) }
    }, [client.empresa, remotyIdEquipamento, currentSigmaKey.codCliente, currentSigmaKey.empresaSigma, hasError, currentRequestKey, lastLoadedKey])
  )

  const errorHandler = (error: Error): void => {
    handleError(error)
    setHasError(true)
  }

  const handleGetRemotyContactList = (requestKey: string): void => {
    const params: GetRemotyContactsParams = {
      idEquipamento: remotyIdEquipamento,
      empresaMonitoramento: client.empresaMonitoramento
    }

    if (requestKey !== lastLoadedKey) {
      setLoading(true)
    }

    getRemotyContacts
      .getContacts(params)
      .then((contacts) => {
        setContactList(contacts)
        setLastLoadedKey(requestKey)
      })
      .catch(errorHandler)
      .finally(() => setLoading(false))
  }

  const handleGetSigmaDeskContactList = (requestKey: string): void => {
    const params: GetSigmaDeskContactsParams = {
      empresaSigma: currentSigmaKey.empresaSigma,
      idCentral: currentSigmaKey.codInterno,
      unidade: currentSigmaKey.empresaSigma
    }

    if (requestKey !== lastLoadedKey) {
      setLoading(true)
    }

    getSigmaDeskContacts
      .getContacts(params)
      .then((contacts) => {
        setContactList(contacts)
        setLastLoadedKey(requestKey)
      })
      .catch(errorHandler)
      .finally(() => setLoading(false))
  }

  const getEventContacts = {
    [ClientMonitoringType.MONI]: () => { },
    [ClientMonitoringType.REMOTY]: handleGetRemotyContactList,
    [ClientMonitoringType.SIGMA_CLOUD]: () => { },
    [ClientMonitoringType.SIGMA_DESK]: handleGetSigmaDeskContactList,
    [ClientMonitoringType.SO_WILL]: () => { }
  }[client.tipoConexao]

  const marginRight = window.innerWidth <= 1380 ? 36 : isMobile ? 'auto' : 0
  const marginLeft = window.innerWidth <= 1380 ? 36 : isMobile ? 'auto' : 0

  return (
    <Box style={{ ...layoutStyles.pageContent }}>
      <PageHeader
        text='Contatos'
        icon={<MaterialCommunityIcons name="alarm-light-outline" size={24} color={theme.colors.grey} />}
        children={isLoading && (
          <HStack items='center' spacing={8}>
            <ActivityIndicator size='small' color={getAccentColor()} />
            <Text color={theme.colors.grey}>Buscando contatos</Text>
          </HStack>
        )}
      />
      <Stack
        direction={isMobile ? 'column-reverse' : 'row'}
        mr={marginRight}
        ml={marginLeft}
        justify='between'
        items='center'
        spacing={8}
      >
        {[ClientMonitoringType.REMOTY].includes(client.tipoConexao) && <SelectRemotyEventCompany disabled={isLoading} />}
        {[ClientMonitoringType.SIGMA_DESK].includes(client.tipoConexao) && <SelectSigmaCloudKey disabled={isLoading} />}
      </Stack>
      <ScrollView style={{ padding: '1rem' }}>
        <CardList
          isEmpty={!contactList.length}
          isLoading={isLoading}
          textLoading='Buscando Eventos de Contatos'
        >
          {contactList?.map(contact => (
            <CardContact
              key={`cardContact-${contact.idDispositivo}`}
              contact={contact}
            />
          ))}
        </CardList>
      </ScrollView>
    </Box>
  )
}
